<template>
  <div>
    <ValidationProvider
      v-slot="{ errors, ariaInput, ariaMsg }"
      rules="required"
      name="audience"
      slim
    >
      <Alert v-if="!hasAPIKey" style="margin-bottom: 1rem">
        <InfoIcon slot="icon" />
        We can't find an Mailchimp API key for your profile.
        <Button
          slot="actions"
          :to="`/profile/${profileId}/settings#integrations`"
          variant="flat"
          color="white"
        >
          Add&nbsp;<span style="font-size: 1.25em">→</span>
        </Button>
      </Alert>

      <template v-else>
        <FormGroup>
          <FormGroupHeader gutter-bottom>
            <Label :for="'mailchimp_list_id' + _uid">Mailchimp Audience</Label>
            <FlexSpacer />
            <v-tooltip top>
              <template #activator="{ on }">
                <QuestionCircleOIcon style="font-size: 20px" v-on="on" />
              </template>
              <span>Choose the list sign ups will be added to</span>
            </v-tooltip>
          </FormGroupHeader>
          <Select
            :id="'mailchimp_list_id' + _uid"
            v-model="listId"
            v-bind="ariaInput"
          >
            <option :value="null">-- Select an audience --</option>
            <option v-for="{ id, name } in lists" :key="id" :value="id">
              {{ name }}
            </option>
          </Select>
          <FormGroupHelp color="error" v-bind="ariaMsg">{{
            errors[0]
          }}</FormGroupHelp>
          <FormGroupHelp v-if="lists === null && !isLoadingLists">
            No audiences have been returned with the provided API key. Double
            check it on
            <router-link :to="`/profile/${profileId}/settings`"
              >the settings page</router-link
            >.
          </FormGroupHelp>
        </FormGroup>
      </template>
    </ValidationProvider>
  </div>
</template>

<script>
import {
  FormGroup,
  FormGroupHeader,
  FormGroupHelp,
  Label,
  FlexSpacer,
  QuestionCircleOIcon,
  Button,
  Alert,
  InfoIcon,
  Select,
} from '@campsite-bio/component-lib';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { ValidationProvider } from 'vee-validate';

import { useCurrentProfile, useStore } from '../../../../compositions';
import { getOptInLists } from '../../../../apis';
import toast from '../../../../utils/toast';
import { getMeta } from '../../../../utils';
import { reportError } from '@/utils/report-error';

export default {
  components: {
    FormGroup,
    FormGroupHeader,
    FormGroupHelp,
    Label,
    FlexSpacer,
    QuestionCircleOIcon,
    Button,
    Alert,
    InfoIcon,
    Select,
    ValidationProvider,
  },

  props: {
    link: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { link } = toRefs(props);
    const store = useStore();
    const lists = ref(null);
    const listId = ref(null);
    const isLoadingLists = ref(true);

    const { profile, id: profileId } = useCurrentProfile();

    const emailOptions = computed(() => {
      return link.value.email_options;
    });

    watch(
      emailOptions,
      ({ destination_id }) => {
        listId.value = destination_id;
      },
      { immediate: true },
    );

    const hasAPIKey = computed(() => {
      return !!getMeta('mailchimp_api_key', null, profile.value.meta);
    });

    const title = computed(() => {
      if (!listId.value || !lists.value) return null;
      return lists.value.find((l) => l.id === listId.value).name;
    });

    async function loadLists() {
      isLoadingLists.value = true;
      try {
        const { data } = await getOptInLists(profileId.value, 'mailchimp');

        lists.value = data.lists;
      } catch (e) {
        const { error_message } = e.response?.data || {};
        reportError(e);
        toast.error(
          error_message ||
            'There was an error trying to get your Mailchimp lists. Please try again soon.',
        );
      }
      isLoadingLists.value = false;
    }

    onMounted(() => {
      if (hasAPIKey.value) loadLists();
    });

    watch(listId, () => {
      emit('change', {
        id: listId.value,
        title: title.value,
      });
    });

    return { hasAPIKey, lists, listId, isLoadingLists, profileId };
  },
};
</script>

<style lang="scss" scoped></style>
